import React from 'react';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const WindowCleaning = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    "Exterior Window Cleaning",
    "Interior Window Cleaning",
    "Screen Cleaning",
    "Sill Cleaning",
    "Track Cleaning",
    "Skylight Cleaning",
    "Hard Water Stain Removal",
    "Solar Panel Cleaning",
    "Light Fixture Cleaning",
    "High Dusting"
  ];

  const otherServices = [
    { name: "House Washing", link: "/services/house-washing" },
    { name: "Roof Washing", link: "/services/roof-washing" },
    { name: "Gutter Cleaning", link: "/services/gutter-cleaning" },
    { name: "Power Washing", link: "/services/power-washing" },
    { name: "Shrink Wrapping", link: "/services/shrink-wrapping" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <ServiceHero
        title="Window Cleaning"
        headline="Dirty Windows?"
        description="Can you see your reflection? Professional residential and commercial window cleaning — interior and exterior."
        backgroundImage="https://customer-assets-v7afamib.emergentagent.net/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/vv6rl6i6_Screenshot%202026-08-05%20at%202.22.55%E2%80%AFPM.png"
        defaultService="Window Cleaning"
      />
      
      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6">
                Our Window Cleaning Services
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When you hire <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>, you're not just getting streak-free glass — 
                you're getting a team that pays attention to every detail: the tracks, the sills, 
                the frames, and even the screens. It's those little touches that make your home 
                feel brighter, fresher, and truly cared for.
              </p>

              <p className="text-gray-700 font-medium mb-4">
                Our suite of residential and commercial services includes:
              </p>

              <ul className="space-y-3 mb-8">
                {services.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="mb-8">
                <p className="font-semibold text-gray-900 mb-2">We Also Offer:</p>
                <p className="text-gray-700">
                  Beyond windows, our team is trusted across the Hamptons for{' '}
                  <strong>House Washing, Roof Washing, Gutter Cleaning, Power Washing, and Shrink Wrapping</strong>.{' '}
                  <Link to="/#services" className="text-blue-600 hover:text-blue-700 font-medium">
                    Explore all our services <ArrowRight className="inline w-4 h-4" />
                  </Link>
                </p>
              </div>

              <button
                onClick={scrollToQuote}
                data-testid="window-cleaning-cta"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Request Quote
              </button>
              <p className="mt-3 text-sm text-gray-600">
                Get within 1 business day
              </p>
            </div>

            {/* Right Images */}
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                {/* Main Image */}
                <div className="relative">
                  <img
                    src="https://customer-assets-v7afamib.emergentagent.net/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/lf1m7ks6_window%20squigi.webp"
                    alt="Professional window cleaning service"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Top Right */}
                  <div className="absolute -top-4 -right-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8" />
                      <div>
                        <div className="text-2xl font-bold">15+ Years</div>
                        <div className="text-sm text-blue-100">Of Experience</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Image */}
                <div className="relative">
                  <img
                    src="/images/before-after/window-cleaning.jpg"
                    alt="Window cleaning before and after"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-xl">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">1450+ Clients</div>
                        <div className="text-sm text-gray-600">Satisfied By Us</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to See Your Reflection?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation quote within 1 business day. 
            Our team is ready to make your windows sparkle.
          </p>
          <Link
            to="/#quote"
            data-testid="window-cleaning-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default WindowCleaning;
